/**
 * Animation effects — reveals, regions, particles, camera, departures.
 *
 * Each effect is a small class with start() / update(dt) / done()
 * methods. The engine runs whichever effects are active each frame.
 *
 * Frame-rate target: ~24fps for smooth motion. We schedule via
 * requestAnimationFrame and cap effective cell updates per frame so
 * heavy effects don't tank perf.
 */

import {
  randomNoiseChar, randomChar, shiftDensity, CHARSET,
} from "./density.js";

// ─── Reveals ────────────────────────────────────────────────────────

/**
 * DecodeFromNoise — every cell starts as random noise; cells lock into
 * their source character in a sweeping pattern.
 *
 * pattern: "left_to_right" | "top_down" | "center_out" | "random"
 */
export class DecodeFromNoise {
  constructor(
    canvas,
    { durationMs = 4000, pattern = "center_out", holdMs = 700, churnRate = 0.18 } = {},
  ) {
    this.canvas = canvas;
    // Total animation length INCLUDES the hold phase. So with
    // durationMs=2400 and holdMs=700, locking happens over the
    // remaining 1700ms.
    this.durationMs = durationMs;
    this.holdMs = holdMs;
    this.pattern = pattern;
    // How aggressively unlocked cells re-roll noise each frame.
    // Higher = more visible static. 0.18 = ~3 noise re-rolls per
    // cell per second, which reads as obvious churn.
    this.churnRate = churnRate;
    this.elapsed = 0;
    this.order = this._buildOrder();
  }

  start() {
    this.canvas.fillWithNoise();
    // Paint the noise immediately so the canvas surface is noise
    // before the browser's next composite. Without this, loadScene's
    // earlier source render is what gets shown on the first paint
    // after kickoff — the user sees a frame of the painting before
    // the rAF tick replaces it with noise, which kills the
    // noise→painting transition.
    this.canvas.render();
    this.elapsed = 0;
  }

  update(dt) {
    this.elapsed += dt;
    // Hold phase — no cells lock, the canvas is pure churning
    // noise. Without this hold, random pattern starts locking
    // cells immediately and the eye never registers a "static"
    // moment before the painting starts emerging.
    let lockCount;
    if (this.elapsed < this.holdMs) {
      lockCount = 0;
    } else {
      const decodeMs = Math.max(1, this.durationMs - this.holdMs);
      const progress = Math.min(1, (this.elapsed - this.holdMs) / decodeMs);
      lockCount = Math.floor(progress * this.order.length);
    }

    for (let i = 0; i < lockCount; i++) {
      const [x, y] = this.order[i];
      this.canvas.set(x, y, this.canvas.getSource(x, y));
    }
    // Cells not yet locked: keep cycling noise. Higher churn rate
    // during the hold makes the static feel alive instead of static.
    for (let i = lockCount; i < this.order.length; i++) {
      if (Math.random() < this.churnRate) {
        const [x, y] = this.order[i];
        this.canvas.set(x, y, randomNoiseChar());
      }
    }
  }

  done() {
    return this.elapsed >= this.durationMs;
  }

  _buildOrder() {
    const { cols, rows } = this.canvas;
    const cells = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        cells.push([x, y]);
      }
    }

    if (this.pattern === "left_to_right") {
      cells.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    } else if (this.pattern === "top_down") {
      cells.sort((a, b) => a[1] - b[1] || a[0] - b[0]);
    } else if (this.pattern === "center_out") {
      const cx = cols / 2;
      const cy = rows / 2;
      cells.sort((a, b) => {
        const da = (a[0] - cx) ** 2 + (a[1] - cy) ** 2;
        const db = (b[0] - cx) ** 2 + (b[1] - cy) ** 2;
        return da - db;
      });
    } else {
      // random
      for (let i = cells.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cells[i], cells[j]] = [cells[j], cells[i]];
      }
    }
    return cells;
  }
}

// ─── Region behaviors ───────────────────────────────────────────────

/**
 * DensityPulseRegion — characters in a bounded region cycle one density
 * step brighter and back at a steady rate.
 *
 * bounds: { x, y, w, h } — top-left corner + extent
 */
export class DensityPulseRegion {
  constructor(canvas, { bounds, rateMs = 3000, amplitude = 1 } = {}) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.rateMs = rateMs;
    this.amplitude = amplitude;
    this.phase = 0;
  }

  start() { this.phase = 0; }

  update(dt) {
    this.phase = (this.phase + dt) % this.rateMs;
    const t = this.phase / this.rateMs;
    // Sine wave: 0 → amplitude → 0 → -amplitude → 0
    const delta = Math.round(Math.sin(t * 2 * Math.PI) * this.amplitude);
    const { x: bx, y: by, w, h } = this.bounds;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const x = bx + dx;
        const y = by + dy;
        const src = this.canvas.getSource(x, y);
        this.canvas.set(x, y, shiftDensity(src, delta));
      }
    }
  }

  done() { return false; }  // continuous
}

/**
 * GlyphCycleRegion — a small region cycles through a fixed list of
 * character states. Used for things like an owl blinking.
 *
 * states: array of strings, each a multi-line glyph representation.
 *   Each line same length; position relative to bounds.x, bounds.y.
 *
 * Or simplified: array of single chars, applied uniformly within bounds.
 */
export class GlyphCycleRegion {
  constructor(canvas, { bounds, states, rateMs = 1500 } = {}) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.states = states;
    this.rateMs = rateMs;
    this.phase = 0;
    this.stateIdx = 0;
  }

  start() { this.phase = 0; this.stateIdx = 0; }

  update(dt) {
    this.phase += dt;
    if (this.phase >= this.rateMs) {
      this.phase = 0;
      this.stateIdx = (this.stateIdx + 1) % this.states.length;
    }
    const ch = this.states[this.stateIdx];
    const { x: bx, y: by, w, h } = this.bounds;

    if (typeof ch === "string" && ch.length === 1) {
      // Single-char state. Always treat the state's character as a
      // target density level: shift each cell's source toward that
      // target by up to ±2 density steps. The painting structure is
      // preserved; the region just "tints" toward sparser or denser.
      //
      // For non-CHARSET characters (e.g. literal "o", "O", "x"), fall
      // back to the densest charset character — these glyphs were
      // historically used as eye-blink markers and read better as a
      // brief darken than as a stamped rectangle of the literal char.
      let targetIdx = CHARSET.indexOf(ch);
      if (targetIdx < 0) {
        targetIdx = CHARSET.length - 1;  // densest
      }
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          const x = bx + dx;
          const y = by + dy;
          const src = this.canvas.getSource(x, y);
          if (src === " ") continue;
          const srcIdx = CHARSET.indexOf(src);
          if (srcIdx < 0) continue;
          const delta = Math.max(-2, Math.min(2, targetIdx - srcIdx));
          this.canvas.set(x, y, shiftDensity(src, delta));
        }
      }
    } else if (typeof ch === "string") {
      // Multi-line glyph: paint relative to bounds origin
      const lines = ch.split("\n");
      for (let dy = 0; dy < lines.length && dy < h; dy++) {
        const line = lines[dy];
        for (let dx = 0; dx < line.length && dx < w; dx++) {
          this.canvas.set(bx + dx, by + dy, line[dx]);
        }
      }
    }
  }

  done() { return false; }
}

/**
 * LocalDriftRegion — characters in a region shift by one cell in a
 * direction at a steady rate. Edge cells wrap or reset to source.
 *
 * direction: "right" | "left" | "up" | "down"
 */
export class LocalDriftRegion {
  constructor(canvas, { bounds, direction = "right", rateMs = 1200 } = {}) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.direction = direction;
    this.rateMs = rateMs;
    this.phase = 0;
    this.offset = 0;
  }

  start() { this.phase = 0; this.offset = 0; }

  update(dt) {
    this.phase += dt;
    if (this.phase >= this.rateMs) {
      this.phase = 0;
      this.offset++;
    }
    const { x: bx, y: by, w, h } = this.bounds;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        let srcX = dx;
        let srcY = dy;
        if (this.direction === "right") srcX = (dx - this.offset + w * 100) % w;
        if (this.direction === "left")  srcX = (dx + this.offset) % w;
        if (this.direction === "down")  srcY = (dy - this.offset + h * 100) % h;
        if (this.direction === "up")    srcY = (dy + this.offset) % h;
        this.canvas.set(bx + dx, by + dy, this.canvas.getSource(bx + srcX, by + srcY));
      }
    }
  }

  done() { return false; }
}

// ─── Particles ──────────────────────────────────────────────────────

/**
 * ParticleSystem — spawns characters at a region edge that drift across
 * the canvas at speed. Used for falling pearls, drifting smoke, etc.
 *
 * spawnRegion: where particles appear
 * direction: "down" | "up" | "left" | "right"
 * chars: array of characters to choose from
 * spawnRateMs: time between spawns
 * speed: cells per second
 */
export class ParticleSystem {
  constructor(canvas, {
    spawnRegion, direction = "down", chars = ["."], speedCellsPerSec = 8,
    spawnRateMs = 500,
  } = {}) {
    this.canvas = canvas;
    this.spawnRegion = spawnRegion;
    this.direction = direction;
    this.chars = chars;
    this.speedCellsPerSec = speedCellsPerSec;
    this.spawnRateMs = spawnRateMs;
    this.particles = [];
    this.spawnPhase = 0;
  }

  start() { this.particles = []; this.spawnPhase = 0; }

  update(dt) {
    // Advance existing particles
    const moveDist = (this.speedCellsPerSec * dt) / 1000;
    for (const p of this.particles) {
      // Restore the cell the particle was on (back to source)
      this.canvas.set(Math.round(p.x), Math.round(p.y),
                       this.canvas.getSource(Math.round(p.x), Math.round(p.y)));
      // Advance
      if (this.direction === "down") p.y += moveDist;
      if (this.direction === "up")   p.y -= moveDist;
      if (this.direction === "right") p.x += moveDist;
      if (this.direction === "left")  p.x -= moveDist;
    }
    // Cull off-canvas
    this.particles = this.particles.filter(p =>
      p.x >= 0 && p.x < this.canvas.cols && p.y >= 0 && p.y < this.canvas.rows
    );

    // Spawn
    this.spawnPhase += dt;
    if (this.spawnPhase >= this.spawnRateMs) {
      this.spawnPhase = 0;
      this._spawn();
    }

    // Render
    for (const p of this.particles) {
      this.canvas.set(Math.round(p.x), Math.round(p.y), p.ch);
    }
  }

  done() { return false; }

  _spawn() {
    const { x, y, w, h } = this.spawnRegion;
    let px, py;
    if (this.direction === "down")  { px = x + Math.random() * w; py = y; }
    else if (this.direction === "up") { px = x + Math.random() * w; py = y + h - 1; }
    else if (this.direction === "right") { px = x; py = y + Math.random() * h; }
    else { px = x + w - 1; py = y + Math.random() * h; }
    const ch = this.chars[Math.floor(Math.random() * this.chars.length)];
    this.particles.push({ x: px, y: py, ch });
  }
}

// ─── Ambient flicker (whole-canvas effect) ──────────────────────────

/**
 * AmbientFlicker — randomly nudges some canvas cells to an adjacent
 * density step. Reads as candlelight or aged-print shimmer.
 *
 * intensity: 0..1 — fraction of cells per second that flicker
 */
export class AmbientFlicker {
  constructor(canvas, { intensity = 0.3, rateMs = 200 } = {}) {
    this.canvas = canvas;
    this.intensity = intensity;
    this.rateMs = rateMs;
    this.phase = 0;
    this.flickered = []; // [{x, y, originalIdx}]
  }

  start() { this.phase = 0; this.flickered = []; }

  update(dt) {
    this.phase += dt;
    if (this.phase < this.rateMs) return;
    this.phase = 0;

    // Reset previously-flickered cells to their source
    for (const f of this.flickered) {
      this.canvas.set(f.x, f.y, this.canvas.getSource(f.x, f.y));
    }
    this.flickered = [];

    // Pick new cells to flicker
    const cellCount = Math.floor(
      this.canvas.cols * this.canvas.rows * this.intensity * 0.05
    );
    for (let i = 0; i < cellCount; i++) {
      const x = Math.floor(Math.random() * this.canvas.cols);
      const y = Math.floor(Math.random() * this.canvas.rows);
      const src = this.canvas.getSource(x, y);
      if (src === " ") continue;
      const delta = Math.random() < 0.5 ? -1 : 1;
      this.canvas.set(x, y, shiftDensity(src, delta));
      this.flickered.push({ x, y });
    }
  }

  done() { return false; }
}

// ─── Vignette (sustained) ───────────────────────────────────────────

/**
 * Vignette — characters near the canvas edges are progressively
 * brightened (toward sparser density), creating a soft halo around
 * the painting's center. Subtle but makes the painting "pop."
 *
 * radius: 0..1, fraction of the half-diagonal where vignette starts
 *   (smaller = vignette comes in earlier)
 * strength: integer density steps applied at full vignette intensity
 */
export class Vignette {
  constructor(canvas, { radius = 0.6, strength = 2 } = {}) {
    this.canvas = canvas;
    this.radius = radius;
    this.strength = strength;
    this.applied = false;
  }

  start() { this.applied = false; }

  update(_dt) {
    if (this.applied) return;  // vignette is static — apply once
    const { cols, rows } = this.canvas;
    const cx = cols / 2;
    const cy = rows / 2;
    const halfDiag = Math.sqrt(cx * cx + cy * cy);
    const innerR = halfDiag * this.radius;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
        if (d <= innerR) continue;  // inside protected area
        const t = Math.min(1, (d - innerR) / (halfDiag - innerR));
        const delta = -Math.round(t * this.strength);  // brighten = lower density
        const src = this.canvas.getSource(x, y);
        this.canvas.set(x, y, shiftDensity(src, delta));
      }
    }
    this.applied = true;
  }

  done() { return false; }
}


// ─── Glitch flash (sustained, occasional) ───────────────────────────

/**
 * GlitchFlash — at random intervals, briefly replaces a small region
 * with random noise, then restores. Reads as a brief signal-loss in
 * the projection.
 *
 * intervalMs: average ms between flashes
 * flashDurationMs: how long each flash lasts
 * size: roughly how many cells per flash
 */
export class GlitchFlash {
  constructor(canvas, {
    intervalMs = 5000, flashDurationMs = 120, size = 200,
  } = {}) {
    this.canvas = canvas;
    this.intervalMs = intervalMs;
    this.flashDurationMs = flashDurationMs;
    this.size = size;
    this.timeToNext = intervalMs;
    this.flashing = false;
    this.flashTimer = 0;
    this.affected = [];
  }

  start() {
    this.timeToNext = this.intervalMs * (0.5 + Math.random());
    this.flashing = false;
  }

  update(dt) {
    if (this.flashing) {
      this.flashTimer += dt;
      if (this.flashTimer >= this.flashDurationMs) {
        // restore
        for (const [x, y] of this.affected) {
          this.canvas.set(x, y, this.canvas.getSource(x, y));
        }
        this.affected = [];
        this.flashing = false;
        this.timeToNext = this.intervalMs * (0.5 + Math.random());
      }
      return;
    }

    this.timeToNext -= dt;
    if (this.timeToNext > 0) return;

    // Trigger flash
    this.flashing = true;
    this.flashTimer = 0;
    const { cols, rows } = this.canvas;
    const baseX = Math.floor(Math.random() * cols);
    const baseY = Math.floor(Math.random() * rows);
    for (let i = 0; i < this.size; i++) {
      const x = baseX + Math.floor((Math.random() - 0.5) * 30);
      const y = baseY + Math.floor((Math.random() - 0.5) * 10);
      if (x < 0 || x >= cols || y < 0 || y >= rows) continue;
      this.canvas.set(x, y, randomChar());
      this.affected.push([x, y]);
    }
  }

  done() { return false; }
}


// ─── Character morph (sustained, region) ────────────────────────────

/**
 * CharacterMorph — region's characters smoothly cycle through density
 * levels, like a slow pulse but more dramatic. Each cell drifts ±2-3
 * density steps from its source on a long sine wave.
 */
export class CharacterMorph {
  constructor(canvas, {
    bounds, periodMs = 5000, amplitude = 2,
  } = {}) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.periodMs = periodMs;
    this.amplitude = amplitude;
    this.phase = 0;
  }

  start() { this.phase = Math.random() * this.periodMs; }

  update(dt) {
    this.phase = (this.phase + dt) % this.periodMs;
    const t = this.phase / this.periodMs;
    const wave = Math.sin(t * 2 * Math.PI);  // -1..1
    const delta = Math.round(wave * this.amplitude);
    const { x: bx, y: by, w, h } = this.bounds;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const x = bx + dx;
        const y = by + dy;
        const src = this.canvas.getSource(x, y);
        if (src === " ") continue;  // don't morph empty cells
        // Cell-specific phase offset for organic feel
        const cellPhase = ((dx * 7 + dy * 13) % 100) / 100;
        const cellWave = Math.sin((t + cellPhase) * 2 * Math.PI);
        const cellDelta = Math.round(cellWave * this.amplitude);
        this.canvas.set(x, y, shiftDensity(src, cellDelta));
      }
    }
  }

  done() { return false; }
}


// ─── Departures ─────────────────────────────────────────────────────

/**
 * FadeToNoise — characters gradually replace themselves with noise then
 * fade to blanks.
 */
export class FadeToNoise {
  constructor(canvas, { durationMs = 2000 } = {}) {
    this.canvas = canvas;
    this.durationMs = durationMs;
    this.elapsed = 0;
    this.order = this._buildOrder();
  }

  start() { this.elapsed = 0; }

  update(dt) {
    this.elapsed += dt;
    const progress = Math.min(1, this.elapsed / this.durationMs);
    // First 60%: replace cells with noise
    // Last 40%: replace noised cells with blank
    if (progress < 0.6) {
      const noiseCount = Math.floor((progress / 0.6) * this.order.length);
      for (let i = 0; i < noiseCount; i++) {
        const [x, y] = this.order[i];
        this.canvas.set(x, y, randomNoiseChar());
      }
    } else {
      const blankProgress = (progress - 0.6) / 0.4;
      const blankCount = Math.floor(blankProgress * this.order.length);
      for (let i = 0; i < blankCount; i++) {
        const [x, y] = this.order[i];
        this.canvas.set(x, y, " ");
      }
    }
  }

  done() { return this.elapsed >= this.durationMs; }

  _buildOrder() {
    const { cols, rows } = this.canvas;
    const cells = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        cells.push([x, y]);
      }
    }
    // Shuffle
    for (let i = cells.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cells[i], cells[j]] = [cells[j], cells[i]];
    }
    return cells;
  }
}

// ─── Painting breath (sustained, whole-canvas pulse) ───────────────

/**
 * PaintingBreath — a slow sine pulse applied to every cell within the
 * painting bounds (auto-detected on canvas load, no manual bounds
 * required). Each cell shifts density by ±amplitude steps as the wave
 * moves; the painting feels like it's breathing.
 *
 * Optimization: tracks the last-applied delta and only rewrites cells
 * when delta changes. With amplitude=1 and a 60fps loop, that means
 * writes happen ~4 times per cycle instead of every frame.
 */
export class PaintingBreath {
  constructor(canvas, { rateMs = 7000, amplitude = 1 } = {}) {
    this.canvas = canvas;
    this.rateMs = rateMs;
    this.amplitude = amplitude;
    this.phase = 0;
    this.lastDelta = 0;
    this.applied = false;
  }

  start() {
    this.phase = 0;
    this.lastDelta = 0;
    this.applied = false;
  }

  update(dt) {
    this.phase = (this.phase + dt) % this.rateMs;
    const t = this.phase / this.rateMs;
    const delta = Math.round(Math.sin(t * 2 * Math.PI) * this.amplitude);
    if (this.applied && delta === this.lastDelta) return;
    this.lastDelta = delta;
    this.applied = true;
    const { x: bx, y: by, w, h } = this.canvas.paintingBounds;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const x = bx + dx;
        const y = by + dy;
        const src = this.canvas.getSource(x, y);
        if (src === " ") continue;
        this.canvas.set(x, y, shiftDensity(src, delta));
      }
    }
  }

  done() { return false; }
}


// ─── Eye blink (focal, discrete close → hold → open) ───────────────

/**
 * EyeBlink — a small region literally closes (cells fade toward sparser
 * density / blank) over closeMs, holds at the closed state for holdMs,
 * then reopens over openMs. Unlike density_pulse this is a discrete
 * three-phase animation with a hard hold in the middle, which reads
 * unambiguously as a blink. The region's source values are cached on
 * start() so reopen restores them exactly.
 *
 * Why this exists: density_pulse with sine + amplitude couldn't reliably
 * convey a wink — at low amplitude it was too subtle, at high amplitude
 * the eye reopened too fast (sine has no hold), and at the densest
 * source cells the shiftDensity clamp meant only some cells changed
 * while others stayed put.
 *
 * Parameters:
 *   bounds:    { x, y, w, h } — region to blink
 *   closeMs:   ms to ramp source → closed (default 250)
 *   holdMs:    ms to stay closed (default 700)
 *   openMs:    ms to ramp closed → source (default 250)
 */
export class EyeBlink {
  constructor(canvas, { bounds, closeMs = 250, holdMs = 700, openMs = 250 } = {}) {
    this.canvas = canvas;
    this.bounds = bounds;
    this.closeMs = closeMs;
    this.holdMs = holdMs;
    this.openMs = openMs;
    this.totalMs = closeMs + holdMs + openMs;
    this.phase = 0;
    this.cache = null;
    this._finished = false;
  }

  start() {
    this.phase = 0;
    this._finished = false;
    const { x: bx, y: by, w, h } = this.bounds;
    this.cache = new Array(h);
    for (let dy = 0; dy < h; dy++) {
      const row = new Array(w);
      for (let dx = 0; dx < w; dx++) {
        row[dx] = this.canvas.getSource(bx + dx, by + dy);
      }
      this.cache[dy] = row;
    }
  }

  update(dt) {
    if (this._finished) return;
    this.phase += dt;
    const { x: bx, y: by, w, h } = this.bounds;

    // Closure level: 0 = open (source), 1 = closed (blank)
    let closure;
    if (this.phase < this.closeMs) {
      closure = this.phase / this.closeMs;
    } else if (this.phase < this.closeMs + this.holdMs) {
      closure = 1;
    } else if (this.phase < this.totalMs) {
      closure = 1 - (this.phase - this.closeMs - this.holdMs) / this.openMs;
    } else {
      // Final restore — paint cached source back, mark done.
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          this.canvas.set(bx + dx, by + dy, this.cache[dy][dx]);
        }
      }
      this._finished = true;
      return;
    }

    // Interpolate each cell from its source density toward index 0
    // (whitespace) based on closure. closure=1 means every non-space
    // cell becomes a literal space — the eye region goes blank.
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const src = this.cache[dy][dx];
        if (src === " ") continue;
        const srcIdx = CHARSET.indexOf(src);
        if (srcIdx < 0) continue;
        const targetIdx = Math.round(srcIdx * (1 - closure));
        this.canvas.set(bx + dx, by + dy, CHARSET[targetIdx]);
      }
    }
  }

  done() { return this._finished; }
}


// ─── Loop sequence (sustained, scheduler) ──────────────────────────

/**
 * LoopSequence — a scheduler that fires sub-effects at timed offsets
 * within a repeating loop window. The painting's "choreography":
 * the owl blinks at t=2s, the basket trembles at t=4.5s, the
 * procession turns at t=7s, all on a 10-second loop.
 *
 * Each event has:
 *   atMs:       when within the loop to fire (0..loopMs)
 *   durationMs: how long the sub-effect lives once fired (optional —
 *               omitted means "run until the loop wraps")
 *   effect:     a sub-effect descriptor passed to makeEffect()
 *
 * On loop wrap, all currently-active sub-effects are cleared so the
 * next iteration starts clean. This means events near the end of a
 * loop window might not get to play their full duration if it bleeds
 * past the wrap — which is intentional: tight loops over loose ones.
 *
 * Errors in sub-effect start()/update() are caught and logged so
 * one buggy event doesn't stop the rest of the choreography.
 */
export class LoopSequence {
  constructor(canvas, { loopMs = 8000, events = [] } = {}) {
    this.canvas = canvas;
    this.loopMs = loopMs;
    // Sort events by atMs so the cross-window scan below is monotonic.
    this.events = events.slice().sort((a, b) => (a.atMs ?? 0) - (b.atMs ?? 0));
    this.elapsed = 0;
    this.active = []; // [{ effect, expiresAt | null }]
  }

  start() {
    this.elapsed = 0;
    this.active = [];
  }

  update(dt) {
    const prevPhase = this.elapsed % this.loopMs;
    this.elapsed += dt;
    const curPhase = this.elapsed % this.loopMs;
    const wrapped = curPhase < prevPhase;

    // Determine which (lo, hi] phase ranges we just crossed. On wrap
    // there are two: (prevPhase, loopMs] and [0, curPhase].
    const ranges = wrapped
      ? [[prevPhase, this.loopMs], [0, curPhase]]
      : [[prevPhase, curPhase]];

    if (wrapped) {
      // Hard reset: clear active sub-effects so the next loop starts
      // from the same baseline, otherwise long-running effects from
      // late in the loop would bleed into early-loop events.
      this.active = [];
    }

    for (const event of this.events) {
      const at = event.atMs ?? 0;
      for (const [lo, hi] of ranges) {
        // Half-open (lo, hi] so an event at exactly atMs=0 fires on
        // the loop wrap (the [0, curPhase] range starts inclusive).
        const inRange = lo === 0 ? (at >= lo && at <= hi) : (at > lo && at <= hi);
        if (!inRange) continue;
        let sub;
        try {
          sub = makeEffect(this.canvas, event.effect);
        } catch (e) {
          console.warn("[loop_sequence] makeEffect threw:", e);
          continue;
        }
        if (!sub) continue;
        try {
          sub.start();
        } catch (e) {
          console.warn("[loop_sequence] sub.start threw:", e);
          continue;
        }
        const expiresAt = event.durationMs
          ? this.elapsed + event.durationMs
          : null;
        this.active.push({ effect: sub, expiresAt });
      }
    }

    // Update active sub-effects, drop ones that are done or expired.
    this.active = this.active.filter(({ effect, expiresAt }) => {
      try {
        effect.update(dt);
      } catch (e) {
        console.warn("[loop_sequence] sub.update threw:", e);
        return false;
      }
      if (effect.done && effect.done()) return false;
      if (expiresAt !== null && this.elapsed >= expiresAt) return false;
      return true;
    });
  }

  done() { return false; }
}


// ─── Effect factory ─────────────────────────────────────────────────

/**
 * Build an effect from a script entry. Lets the engine parse animation
 * scripts declaratively.
 */
export function makeEffect(canvas, descriptor) {
  switch (descriptor.type) {
    case "decode_from_noise":
      return new DecodeFromNoise(canvas, descriptor);
    case "density_pulse":
      return new DensityPulseRegion(canvas, descriptor);
    case "glyph_cycle":
      return new GlyphCycleRegion(canvas, descriptor);
    case "local_drift":
      return new LocalDriftRegion(canvas, descriptor);
    case "particles":
      return new ParticleSystem(canvas, descriptor);
    case "ambient_flicker":
      return new AmbientFlicker(canvas, descriptor);
    case "vignette":
      return new Vignette(canvas, descriptor);
    case "glitch_flash":
      return new GlitchFlash(canvas, descriptor);
    case "character_morph":
      return new CharacterMorph(canvas, descriptor);
    case "fade_to_noise":
      return new FadeToNoise(canvas, descriptor);
    case "loop_sequence":
      return new LoopSequence(canvas, descriptor);
    case "eye_blink":
      return new EyeBlink(canvas, descriptor);
    case "painting_breath":
      return new PaintingBreath(canvas, descriptor);
    default:
      console.warn(`[ascii] unknown effect type: ${descriptor.type}`);
      return null;
  }
}
